import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import mermaid from "mermaid";
import { useLearningArticle } from "../../../../hooks/useLearningArticles";
import { parseLearningArticleMarkdown } from "../learn/learningArticleMarked";
import { ArticleImageLightbox } from "../learn/ArticleImageLightbox";

/**
 * Loads a support article by slug (category **inline** or **learning**) via GET /learning-articles?slug=…
 * and shows markdown + optional Listen audio — same pattern as Help Center articles.
 */
export default function InlineCoachArticleDialog({ slug, open, onClose, titleFallback = "Guide" }) {
  const { data: article, isLoading, isError } = useLearningArticle(slug || undefined, {
    enabled: open && !!slug,
  });
  const contentRef = useRef(null);
  const [imageLightbox, setImageLightbox] = useState(null);

  const closeImageLightbox = useCallback(() => setImageLightbox(null), []);

  const htmlContent = useMemo(
    () => parseLearningArticleMarkdown(article?.content),
    [article?.content],
  );

  useEffect(() => {
    if (!open || !htmlContent || !contentRef.current) return;

    const mermaidBlocks = contentRef.current.querySelectorAll(".mermaid");
    if (!mermaidBlocks.length) return;

    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "strict",
      theme: "default",
    });

    mermaid.run({ nodes: Array.from(mermaidBlocks) }).catch((err) => {
      console.error("Failed to render mermaid in inline article:", err);
    });
  }, [htmlContent, open]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || !htmlContent) return;

    const onClick = (e) => {
      const t = e.target;
      if (t.tagName !== "IMG") return;
      e.preventDefault();
      e.stopPropagation();
      setImageLightbox({
        src: t.currentSrc || t.src,
        alt: t.alt || "",
      });
    };

    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [htmlContent]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle sx={{ pr: 5 }}>
          {open && article?.title ? article.title : titleFallback}
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          {open && !isLoading && isError && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This guide isn&apos;t available yet, or your team hasn&apos;t published it. Expand{" "}
              <strong>Read more</strong> on the task for the full step list, or ask your admin to publish the
              inline article in m45-admin (category <strong>inline</strong>, slug{" "}
              <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
                {slug}
              </Typography>
              ).
            </Alert>
          )}
          {open && !isLoading && article && (
            <>
              {article.listen && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    mb: 2,
                    p: 1,
                    borderRadius: 2,
                    bgcolor: "#6366f108",
                    border: "1px solid #6366f120",
                  }}
                >
                  <HeadphonesIcon sx={{ fontSize: 18, color: "#6366f1", flexShrink: 0 }} />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: "#6366f1", whiteSpace: "nowrap" }}
                  >
                    Listen
                  </Typography>
                  <Box
                    key={article.listen}
                    component="audio"
                    controls
                    preload="none"
                    src={article.listen}
                    sx={{ height: 32, flex: 1, minWidth: 0 }}
                  />
                </Stack>
              )}
              <Box
                ref={contentRef}
                sx={{
                  "& h1": { fontSize: "1.5rem", fontWeight: 700, mt: 3, mb: 1 },
                  "& h2": { fontSize: "1.25rem", fontWeight: 600, mt: 2.5, mb: 1 },
                  "& h3": { fontSize: "1.1rem", fontWeight: 600, mt: 2, mb: 0.75 },
                  "& p": { lineHeight: 1.7, mb: 1.5, color: "text.secondary" },
                  "& ul, & ol": { pl: 3, mb: 1.5, color: "text.secondary" },
                  "& li": { mb: 0.5, lineHeight: 1.6 },
                  "& code": {
                    bgcolor: "action.hover",
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: "0.85em",
                  },
                  "& img": {
                    maxWidth: "100%",
                    height: "auto",
                    display: "block",
                    borderRadius: 1,
                    my: 1,
                    cursor: "zoom-in",
                  },
                  "& video.learn-article-video": {
                    maxWidth: "100%",
                    height: "auto",
                    display: "block",
                    borderRadius: 1,
                    my: 1,
                    backgroundColor: "#000",
                  },
                  "& a": { color: "primary.main" },
                  "& .mermaid": { overflowX: "auto", my: 2 },
                }}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
      <ArticleImageLightbox
        open={Boolean(imageLightbox)}
        src={imageLightbox?.src}
        alt={imageLightbox?.alt}
        onClose={closeImageLightbox}
      />
    </>
  );
}
